import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {GetTicketModel, TicketAttachmentModel, TicketModel} from '@app/model/ticket.model';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { GlobalSpinnerService } from '@app/services/misc-services/global-spinner.service';
import { CreditUnionService } from '@app/services/credit-union/credit-union.service';
import { Router } from '@angular/router';
import {TicketsService} from "@app/services/tickets/tickets.service";
import {PortalUser} from "@app/model/portal-user.model";
import {LoginService} from "@app/login/login.service";
import {BehaviorSubject, fromEvent, Observable} from "rxjs";
import {CreditUnion} from "@app/pages/admin/admin.component";
import {FormControl} from "@angular/forms";
import {ExportTableComponent} from "@app/commons/export-table/export-table.component";
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {debounceTime, distinctUntilChanged, filter, map, startWith, tap} from "rxjs/operators";

@Component({
  selector: 'flex-tickets',
  templateUrl: './tickets.component.html',
  styleUrls: ['./tickets.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketsComponent implements OnInit, AfterViewInit {
  activeUser: BehaviorSubject<PortalUser> = new BehaviorSubject<PortalUser>(null);
  canChangeCUID: boolean = false;
  allPortalUsers: BehaviorSubject<PortalUser[]> = new BehaviorSubject<PortalUser[]>([]);
  columns: string[] = ['ticketNumber', 'title', 'requestedBy', 'status', 'createdTime', 'lastUpdated', 'supportRep'];
  displayedColumns: Array<{ label: string; value: string | ((rowItem: TicketModel, additionalData?: any) => any) }> = [
    { label: 'Ticket', value: 'ticketNumber' },
    { label: 'Title', value: 'title' },
    { label: 'Requested By', value: 'requestedBy'},
    { label: 'Status', value: 'status' },
    { label: 'Created', value: 'createdTime'},
    { label: 'Last Updated', value: 'lastUpdated' },
    { label: 'Support Rep', value: 'supportRep' },
  ];
  statusFilters = [
    { label: 'Open', value: 'O', include: true},
    { label: 'Closed', value: 'C', include: false},
    { label: 'Waiting', value: 'W', include: false},
    { label: 'HIDDEN', value: 'I', include: false}
  ];
  typeFilters = [
    { label: 'General Support', value: '', include: false},
    { label: 'Paid Enhancement', value: 'E', include: false},
    { label: 'Community Enhancement', value: 'C', include: false},
  ]
  dateColumn: string[] = ['Created', 'Last Updated'];
  cuidSearch: FormControl = new FormControl();
  creditUnionsSearch: Observable<CreditUnion[]> = new Observable<CreditUnion[]>()
  creditUnions: CreditUnion[] = [];
  selectedCuid: string;
  pageSizeOptions: number[] = [];
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('input') input: ElementRef;
  createdByFromDate: Date;
  createdByToDate: Date;
  exportTableDialogRef: MatDialogRef<ExportTableComponent>;
  exportTableDialogConfig = new MatDialogConfig();
  constructor(
    public ticketsService: TicketsService,
    public router: Router,
    private globalSpinner: GlobalSpinnerService,
    private creditUnionService: CreditUnionService,
    private userService: LoginService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.allPortalUsers = this.creditUnionService.getAllPortalUsers();
    this.userService.getActiveUserSubject().subscribe((user) => {
      this.activeUser.next(user);
      if (user.superAdmin) {
        this.canChangeCUID = true;
      }
      if (this.ticketsService.tickets.data.length === 0) {
        this.getTickets(this.activeUser.value.cuid);
      }
      this.applyPredicate();
      this.applyFilter();
      this.creditUnionService.getAllCreditUnions().subscribe((creditUnionList: CreditUnion[]) => {
        this.creditUnions = creditUnionList;
        this.creditUnionsSearch = this.cuidSearch.valueChanges.pipe(
            startWith(''),
            map(value => this.filterCreditUnionList(value))
        )
      })
    })
  }

  ngAfterViewInit() {
    fromEvent(this.input.nativeElement, 'keyup')
        .pipe(
            filter(Boolean),
            debounceTime(350),
            distinctUntilChanged(),
            tap(() => {
              this.applyFilter(this.input.nativeElement.value)
            })
        ).subscribe()
  }

  filterCreditUnionList(searchValue: string) {
    const value = searchValue.toLowerCase();
    return this.creditUnions.filter((cu) => {
      if (cu.cuid.toLowerCase().includes(value) || cu.name.toLowerCase().includes(value)) {
        return cu;
      }
    })
  }

  getTickets(cuid: string) {
      this.globalSpinner.addEvent('getTickets', 'Loading Tickets');
      this.ticketsService.ticketsByCUID(cuid).subscribe((tickets) => {
        this.ticketsService.tickets.data = tickets;
        this.ticketsService.pageSizeOptions = [15, 30, 50, this.ticketsService.tickets.data.length];
        this.ticketsService.tickets.paginator = this.paginator;
        this.ticketsService.tickets.sort = this.sort;
        this.globalSpinner.removeAllEvent()
      });
  }

  editTicket(ticketNumber: number) {
    this.ticketsService.getTicketAndAttachments(ticketNumber).then((ticketAndAttachments) => {
      ticketAndAttachments[0].subscribe((ticket: GetTicketModel) => {
        this.ticketsService.selectedTicket.next(ticket)
        this.router.navigate(['/ticket/view'])
      });
      ticketAndAttachments[1].subscribe((attachments: TicketAttachmentModel[]) =>
          this.ticketsService.selectedTicketAttachments.next(attachments)
      )
    })
  }

  newTicket() {
    this.ticketsService.selectedTicket.next(null);
    this.ticketsService.selectedTicketAttachments.next([]);
    this.router.navigate(['/new-ticket']).then(() => {});
  }

  searchTable(searchString: string) {
    this.ticketsService.tickets.filter = searchString.trim().toLowerCase();
  }

  clearCheckbox() {
    this.statusFilters.forEach((status) => status.include = false)
    this.typeFilters.forEach((type) => type.include = false)
    this.applyPredicate();
    this.applyFilter();
  }

  exportTable() {
    this.exportTableDialogConfig.data = {
      displayedColumns: this.displayedColumns,
      selectedUsers: this.ticketsService.tickets.filteredData
    }
    this.exportTableDialogConfig.width = '1000px';
    this.exportTableDialogConfig.height = 'auto';
    this.exportTableDialogRef = this.dialog.open(ExportTableComponent, this.exportTableDialogConfig);
  }

  updateFilters(event, filterObj: {label: string, value: string, include: boolean}) {
    filterObj.include = event.checked;
    if (filterObj.value === 'C') {
      this.statusFilters[3].include = event.checked;
    }
    this.applyPredicate();
    this.applyFilter();
  }

  applyPredicate():void {
    this.ticketsService.tickets.filterPredicate = (data: any, filterString:string):boolean => {
      const filterObject : FilterObject = JSON.parse(filterString)
      let inDateRange: boolean = true;
      let typeMatch: boolean = true;
      let statusMatch: boolean = true;
      let search: boolean = true;
      if(data.ticketNumber === 711830) {
        console.log(data)
      }
      if(filterObject.dateRange.fromDate || filterObject.dateRange.toDate){
        if(data.openDate){
          inDateRange = this.checkBetweenDateRange(data.openDate, filterObject.dateRange.fromDate, filterObject.dateRange.toDate)
        }else {
          inDateRange = this.checkBetweenDateRange(data.createdTime, filterObject.dateRange.fromDate, filterObject.dateRange.toDate)
        }
        if(!inDateRange){
          return false
        }
      }
      if(filterObject.status[0]){
        statusMatch = filterObject.status.includes(data.status)
        if(!statusMatch){
          return false
        }
      }
      if(filterObject.type){
        if(filterObject.type[0]){
          typeMatch = filterObject.type.includes(data.type)
        }
        if(!typeMatch){
          return false
        }
      }
      if(filterObject.searchText){
        search = false
        for(const[key, value] of Object.entries(data)){
          if(value) {
            if(value.toString().toLowerCase().includes(filterObject.searchText)){
              if(!key.includes('Time')) {
                search = true
              }
            }
          }
        }
        return search
      }
      return true
    }
  }

  applyFilter(searchString = '') {
    let filterObject: FilterObject = new FilterObject();
    // filterObject.type = this.typeFilters.filter(type => type.include).map(type => type.value);
    filterObject.status = this.statusFilters.filter(status => status.include).map(status => status.value);
    filterObject.dateRange = {fromDate: this.createdByFromDate, toDate: this.createdByToDate};
    if (filterObject.dateRange.toDate) {
      filterObject.dateRange.toDate.setHours(23,59);
    }
    filterObject.searchText = searchString.toLowerCase()
    this.ticketsService.tickets.filter = JSON.stringify(filterObject);
  }

  checkBetweenDateRange(evalDate: Date, startDate: Date, endDate: Date):boolean {
    let checkDate = new Date(evalDate);
    let start = new Date(startDate);
    let end = new Date(endDate);
    end.setMinutes(59)
    end.setHours(23)
    end.setSeconds(59)
    if(startDate && endDate) {
      return (start.getTime() <= checkDate.getTime() && checkDate.getTime() <= end.getTime())
    }
    if(startDate){
      return (start.getTime() <= checkDate.getTime())
    }
    if(endDate){
      return (start.getTime() >= end.getTime())
    }
    return false
  }

  allCheckBoxSelected() {
    let allValue = true;
    this.typeFilters.forEach((type) => {
      if (type.include) {
        allValue = false;
      }
    })
    this.statusFilters.forEach((status) => {
      if (status.include) {
        allValue = false;
      }
    })
    return allValue;
  }
}

export class FilterObject {
  status: string[] = [];
  type: string[] = [];
  searchText: string = ''
  dateRange: {
    fromDate: Date,
    toDate: Date
  };
}
